/**
 * JSON-Render Component Registry
 *
 * React components that render the JSON elements from the AI.
 * Matches existing marketplace styling from ResultGrid.jsx
 */

import React, { memo, useState, useCallback, useEffect, useRef, useLayoutEffect, useMemo } from 'react';
import { Sparkles, Leaf, Award, Star, ShoppingCart, Eye, Heart, Bell, Check, Palette, TrendingDown } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

import { usePreviewPanel } from '../../contexts/PreviewPanelContext';
import { useWallet } from '../../contexts/WalletContext';
import { useProductReactions } from '../../hooks/useProductReactions';

import { useLayoutContext, useChatCallbacks } from './Providers';

// Resolve image URLs - prepend control plane for relative /assets/ paths
const CONTROL_PLANE_URL = 'https://agentic-commerce-control-plane.pages.dev';
const resolveImageUrl = (imageUrl) => {
  if (!imageUrl) return '/images/placeholder.svg';
  if (imageUrl.startsWith('/assets/')) {
    return `${CONTROL_PLANE_URL}${imageUrl}`;
  }
  return imageUrl;
};

// Quality tier badge configuration
const QUALITY_BADGES = {
  luxury: { label: 'Luxury', className: 'bg-amber-100 text-amber-800', icon: Award },
  premium: { label: 'Premium', className: 'bg-purple-100 text-purple-800', icon: Star },
  'mid-range': { label: 'Quality', className: 'bg-blue-100 text-blue-800', icon: null },
  budget: { label: 'Value', className: 'bg-green-100 text-green-800', icon: Leaf },
};

/**
 * ProductCard - Single product display with actions
 */
const ProductCard = memo(function ProductCard({ element, onAction }) {
  const { props } = element;
  const badge = props.qualityTier ? QUALITY_BADGES[props.qualityTier.toLowerCase()] : null;
  const BadgeIcon = badge?.icon;

  const handleAddToCart = (e) => {
    e.stopPropagation();
    onAction?.({ name: 'add_to_cart', params: { productId: props.productId, quantity: 1 } });
  };

  const handleViewDetails = () => {
    onAction?.({ name: 'view_details', params: { productId: props.productId } });
  };

  const handleSaveForLater = (e) => {
    e.stopPropagation();
    onAction?.({ name: 'save_for_later', params: { productId: props.productId } });
  };

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      onClick={handleViewDetails}
      className="flex flex-col cursor-pointer p-3 overflow-hidden bg-white border border-neutral-200 hover:border-penta-mint transition-colors duration-200 group"
    >
      {/* Square image container with fixed aspect ratio */}
      <div className="relative w-full pt-[100%] overflow-hidden mb-1">
        <img
          src={resolveImageUrl(props.image)}
          alt={props.title || 'Product image'}
          className="absolute inset-0 w-full my-auto object-cover"
          loading="lazy"
          onError={(e) => {
            e.target.src = '/images/placeholder.svg';
          }}
        />
        {/* Quality tier badge overlay */}
        {badge && (
          <div className={`absolute top-1 left-1 px-1.5 py-0.5 text-xs font-medium rounded flex items-center gap-1 ${badge.className}`}>
            {BadgeIcon && <BadgeIcon size={10} />}
            {badge.label}
          </div>
        )}
        {/* Enriched indicator */}
        {props.isEnriched && (
          <div className="absolute top-1 right-1 p-1 bg-white/90 rounded-full" title="AI Enhanced">
            <Sparkles size={12} className="text-penta-mint" />
          </div>
        )}
        {/* Action buttons on hover */}
        <div className="absolute bottom-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={handleSaveForLater}
            className="p-1.5 bg-white rounded-full shadow hover:bg-penta-mint hover:text-penta-on-accent transition-colors"
            title="Save for later"
          >
            <Heart size={14} />
          </button>
          <button
            onClick={handleAddToCart}
            className="p-1.5 bg-white rounded-full shadow hover:bg-penta-mint hover:text-penta-on-accent transition-colors"
            title="Add to cart"
          >
            <ShoppingCart size={14} />
          </button>
        </div>
      </div>

      {/* Brand */}
      {props.brand && (
        <div className="text-xs text-penta-grey-medium truncate mt-1 uppercase tracking-wide">
          {props.brand}
        </div>
      )}

      {/* Truncated title on a single line */}
      <div className="text-sm truncate text-penta-black">{props.title}</div>

      {/* Key feature (if available) */}
      {props.keyFeatures?.[0] && (
        <div className="text-xs text-penta-grey-medium truncate">{props.keyFeatures[0]}</div>
      )}

      {/* Condition badge for pre-owned */}
      {props.condition && props.condition !== 'new' && (
        <div className="mt-1">
          <span className="text-xs px-1.5 py-0.5 bg-penta-grey-light text-penta-grey-medium rounded">
            {props.condition.replace('_', ' ')}
          </span>
        </div>
      )}

      {/* Price */}
      <div className="text-penta-grey-medium text-sm mt-1 flex items-center gap-2">
        <span className="font-medium text-penta-black">${props.price?.toFixed(2)}</span>
        {props.originalPrice && props.originalPrice > props.price && (
          <span className="line-through text-xs">${props.originalPrice?.toFixed(2)}</span>
        )}
      </div>

      {/* AI Explanation - streams in progressively */}
      {props.explanation && (
        <div className="mt-2 pt-2 border-t border-neutral-100">
          <div className="flex items-start gap-1.5">
            <Sparkles size={12} className="text-penta-mint mt-0.5 flex-shrink-0" />
            <p className="text-xs text-penta-grey-medium leading-relaxed line-clamp-3">
              {props.explanation}
            </p>
          </div>
        </div>
      )}
    </div>
  );
});

/**
 * InlineProductCard - Compact card for conversation flow carousel
 * Clean, consistent cards without inline explanations (chat message provides context)
 */
const InlineProductCard = memo(function InlineProductCard({ element, onAction, compareSelected, onCompareToggle }) {
  const { props } = element;
  const { openProduct } = usePreviewPanel();
  const badge = props.qualityTier ? QUALITY_BADGES[props.qualityTier.toLowerCase()] : null;
  const BadgeIcon = badge?.icon;

  const handleViewDetails = () => {
    // Open product detail in preview panel (explanation shows there)
    openProduct(props.productId, {
      id: props.productId,
      productId: props.productId,
      title: props.title,
      brand: props.brand,
      price: props.price,
      image: props.image,
      description: props.description,
      explanation: props.explanation,
      qualityTier: props.qualityTier,
      condition: props.condition,
      sustainability: props.sustainability,
    });
    onAction?.({ name: 'view_details', params: { productId: props.productId } });
  };

  const handleCompareToggle = (e) => {
    e.stopPropagation();
    onCompareToggle?.();
  };

  // Inline carousel mode - clean, consistent card without explanation (chat provides context)
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      onClick={handleViewDetails}
      className={`flex-shrink-0 w-40 cursor-pointer bg-white border transition-all duration-200 group rounded-lg overflow-hidden hover:shadow-md ${
        compareSelected ? 'border-penta-mint ring-1 ring-penta-mint' : 'border-neutral-200 hover:border-penta-mint'
      }`}
    >
      {/* Square image */}
      <div className="relative w-full aspect-square overflow-hidden bg-neutral-50">
        <img
          src={resolveImageUrl(props.image)}
          alt={props.title || 'Product image'}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => {
            e.target.src = '/images/placeholder.svg';
          }}
        />
        {/* Quality tier badge */}
        {badge && (
          <div className={`absolute top-1 left-1 px-1.5 py-0.5 text-[10px] font-medium rounded flex items-center gap-0.5 ${badge.className}`}>
            {BadgeIcon && <BadgeIcon size={10} />}
            {badge.label}
          </div>
        )}
        {/* Compare checkbox overlay */}
        {onCompareToggle && (
          <button
            onClick={handleCompareToggle}
            className={`absolute top-1 right-1 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors bg-white/90 ${
              compareSelected
                ? 'bg-penta-mint border-penta-mint text-penta-on-accent'
                : 'border-neutral-300 hover:border-penta-mint'
            }`}
            title={compareSelected ? 'Remove from comparison' : 'Add to comparison'}
          >
            {compareSelected && <Check size={10} />}
          </button>
        )}
      </div>

      {/* Compact details - no explanation, consistent height */}
      <div className="p-2.5">
        {/* Brand */}
        {props.brand && (
          <div className="text-[10px] text-penta-grey-medium uppercase tracking-wide truncate">
            {props.brand}
          </div>
        )}
        {/* Title - 2 lines max for consistency */}
        <div className="text-xs font-medium text-penta-black line-clamp-2 leading-snug h-8">
          {props.title}
        </div>
        {/* Price */}
        <div className="text-sm font-semibold text-penta-black mt-1">
          ${props.price?.toFixed(2)}
        </div>
      </div>
    </div>
  );
});

/**
 * ProductGrid - Grid layout for multiple products
 * Supports both grid mode (default) and inline conversation mode
 */
const ProductGrid = memo(function ProductGrid({ element, children }) {
  const { props } = element;
  const layoutContext = useLayoutContext();
  const { openGrid, openCompare } = usePreviewPanel();
  const columns = props.columns || 3;
  // Use inline mode if prop set OR if rendering in inline context (conversation flow)
  const isInline = props.layout === 'inline' || layoutContext.inline;

  // Compare selection state
  const [selectedForCompare, setSelectedForCompare] = useState([]);

  // Get product data from children for "See all" functionality
  const childCount = React.Children.count(children);
  const showSeeAll = childCount > 0;

  // Extract all product data from children
  const extractProducts = useCallback(() => {
    const products = [];
    React.Children.forEach(children, (child) => {
      if (child?.props?.element?.props) {
        const p = child.props.element.props;
        products.push({
          id: p.productId,
          productId: p.productId,
          title: p.title,
          brand: p.brand,
          price: p.price,
          image: p.image,
          description: p.description,
          explanation: p.explanation,
          qualityTier: p.qualityTier,
          condition: p.condition,
          sustainability: p.sustainability,
        });
      }
    });
    return products;
  }, [children]);

  const handleSeeAll = () => {
    openGrid(extractProducts(), props.title || 'All Products');
  };

  const handleCompare = () => {
    const allProducts = extractProducts();
    const productsToCompare = allProducts.filter(p =>
      selectedForCompare.includes(p.productId)
    );
    openCompare(productsToCompare);
    setSelectedForCompare([]);
  };

  const toggleCompareSelection = useCallback((productId) => {
    setSelectedForCompare(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : prev.length < 4 ? [...prev, productId] : prev
    );
  }, []);

  const gridClasses = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
  };

  // Inline mode: horizontal carousel for conversation flow
  if (isInline) {
    // Clone children to pass compare selection props
    const childrenWithCompare = React.Children.map(children, (child) => {
      if (!child?.props?.element?.props?.productId) return child;
      const productId = child.props.element.props.productId;
      return React.cloneElement(child, {
        compareSelected: selectedForCompare.includes(productId),
        onCompareToggle: () => toggleCompareSelection(productId),
      });
    });

    return (
      <div className="space-y-2">
        {props.title && (
          <p className="text-sm text-penta-grey-medium">{props.title}</p>
        )}
        {/* Horizontal scrolling carousel - conversational "how about these?" style */}
        <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-neutral-300 scrollbar-track-transparent -mx-2 px-2">
          {childrenWithCompare}
        </div>
        <div className="flex items-center gap-3 mt-1">
          {showSeeAll && (
            <button
              onClick={handleSeeAll}
              className="text-sm text-penta-mint hover:underline"
            >
              See all {childCount} options →
            </button>
          )}
          {selectedForCompare.length >= 2 && (
            <button
              onClick={handleCompare}
              className="text-sm bg-penta-mint text-penta-on-accent px-3 py-1 rounded-full hover:bg-penta-mint/90"
            >
              Compare {selectedForCompare.length} products
            </button>
          )}
        </div>
      </div>
    );
  }

  // Default grid mode
  return (
    <div className="my-4">
      {props.title && (
        <h3 className="text-lg font-semibold text-penta-black mb-2">{props.title}</h3>
      )}
      {props.subtitle && (
        <p className="text-sm text-penta-grey-medium mb-3">{props.subtitle}</p>
      )}
      <div className={`grid ${gridClasses[columns] || gridClasses[3]} gap-4`}>
        {children}
      </div>
    </div>
  );
});

/**
 * CompareTable - Side-by-side product comparison
 * T034: Enhanced with narrative, diff highlighting, dismiss, and purchase CTA
 * T039: Emits comparison.created and comparison.product_selected TES events
 */
const CompareTable = memo(function CompareTable({ element, children, onAction }) {
  const { props } = element;
  const attributes = props.attributes || ['price', 'brand', 'condition'];
  const highlightDiffs = props.highlightDiffs || false;
  const [dismissedIds, setDismissedIds] = useState(new Set());
  const hasTESFired = useRef(false);

  // T039: Emit comparison.created TES event on mount
  useEffect(() => {
    if (!hasTESFired.current) {
      hasTESFired.current = true;
      // Extract product IDs from children
      const productIds = [];
      React.Children.forEach(children, (child) => {
        const pid = child?.props?.element?.props?.productId;
        if (pid) productIds.push(pid);
      });
      console.log('[TES Event] comparison.created', { product_ids: productIds });
    }
  }, [children]);

  // Compute price extremes for diff highlighting
  const priceExtremes = useMemo(() => {
    if (!highlightDiffs) return {};
    const prices = [];
    React.Children.forEach(children, (child) => {
      const p = child?.props?.element?.props?.price;
      if (typeof p === 'number') prices.push(p);
    });
    if (prices.length < 2) return {};
    return { min: Math.min(...prices), max: Math.max(...prices) };
  }, [children, highlightDiffs]);

  const handleDismiss = (productId) => {
    setDismissedIds(prev => {
      const next = new Set(prev);
      next.add(productId);
      return next;
    });

    // Calculate remaining IDs
    const remainingIds = [];
    React.Children.forEach(children, (child) => {
      const pid = child?.props?.element?.props?.productId;
      if (pid && pid !== productId && !dismissedIds.has(pid)) {
        remainingIds.push(pid);
      }
    });

    onAction?.({
      name: 'dismiss_from_comparison',
      params: { productId, remainingIds },
    });
  };

  // T039: "I'll take this one" CTA
  const handleSelect = (productId) => {
    console.log('[TES Event] comparison.product_selected', { product_id: productId });
    onAction?.({ name: 'quick_buy', params: { productId } });
  };

  // Filter out dismissed children
  const visibleChildren = React.Children.toArray(children).filter((child) => {
    const pid = child?.props?.element?.props?.productId;
    return !pid || !dismissedIds.has(pid);
  });

  // Clone children to inject comparison props
  const enhancedChildren = visibleChildren.map((child) => {
    const childProps = child?.props?.element?.props;
    if (!childProps?.productId) return child;

    const price = childProps.price;
    let priceDiffClass = '';
    if (highlightDiffs && typeof price === 'number' && priceExtremes.min !== priceExtremes.max) {
      if (price === priceExtremes.min) priceDiffClass = 'bg-green-50 ring-1 ring-green-200';
      else if (price === priceExtremes.max) priceDiffClass = 'bg-red-50 ring-1 ring-red-200';
    }

    return (
      <div key={childProps.productId} className={`relative ${priceDiffClass} rounded-lg`}>
        {/* Dismiss button */}
        <button
          onClick={(e) => { e.stopPropagation(); handleDismiss(childProps.productId); }}
          className="absolute top-1 right-1 z-10 w-6 h-6 flex items-center justify-center bg-white/90 rounded-full shadow text-penta-grey-medium hover:text-penta-black hover:bg-white transition-colors"
          title="Remove from comparison"
        >
          ×
        </button>
        {child}
        {/* "I'll take this one" CTA */}
        <div className="px-2 pb-2">
          <button
            onClick={(e) => { e.stopPropagation(); handleSelect(childProps.productId); }}
            className="w-full py-2 text-sm bg-penta-mint text-penta-on-accent rounded-full hover:bg-penta-mint/90 transition-colors"
          >
            I&apos;ll take this one
          </button>
        </div>
      </div>
    );
  });

  return (
    <div className="my-4 overflow-x-auto">
      {props.title && (
        <h3 className="text-lg font-semibold text-penta-black mb-3">{props.title}</h3>
      )}
      {/* T034: AI narrative */}
      {props.narrative && (
        <div className="mb-4 p-3 bg-penta-grey-light/30 rounded-lg border border-neutral-100">
          <ReactMarkdown className="prose prose-sm max-w-none text-penta-black">
            {props.narrative}
          </ReactMarkdown>
        </div>
      )}
      <div className="flex gap-4 min-w-max">
        {enhancedChildren}
      </div>
      {visibleChildren.length < 2 && (
        <p className="mt-3 text-sm text-penta-grey-medium">
          Not enough products to compare. Search for more options?
        </p>
      )}
      <div className="mt-4 text-sm text-penta-grey-medium">
        Comparing: {attributes.join(', ')}
      </div>
    </div>
  );
});

/**
 * FeaturedProduct - Hero product display
 */
const FeaturedProduct = memo(function FeaturedProduct({ element, onAction }) {
  const { props } = element;

  const handleViewDetails = () => {
    onAction?.({ name: 'view_details', params: { productId: props.productId } });
  };

  const handleAddToCart = () => {
    onAction?.({ name: 'add_to_cart', params: { productId: props.productId, quantity: 1 } });
  };

  return (
    <div className="my-4 p-4 bg-white border border-neutral-200 rounded-lg">
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Image gallery */}
        <div className="lg:w-1/2">
          <div className="aspect-square overflow-hidden rounded-lg">
            <img
              src={resolveImageUrl(props.images?.[0])}
              alt={props.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = '/images/placeholder.svg';
              }}
            />
          </div>
          {props.images?.length > 1 && (
            <div className="flex gap-2 mt-2 overflow-x-auto">
              {props.images.slice(1, 5).map((img, i) => (
                <img
                  key={i}
                  src={resolveImageUrl(img)}
                  alt={`${props.title} ${i + 2}`}
                  className="w-16 h-16 object-cover rounded"
                />
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div className="lg:w-1/2">
          <div className="text-sm text-penta-grey-medium uppercase tracking-wide">{props.brand}</div>
          <h2 className="text-2xl font-semibold text-penta-black mt-1">{props.title}</h2>
          <div className="text-2xl font-bold text-penta-mint mt-2">${props.price?.toFixed(2)}</div>

          <p className="text-penta-grey-medium mt-4">{props.description}</p>

          {props.features?.length > 0 && (
            <ul className="mt-4 space-y-1">
              {props.features.map((feature, i) => (
                <li key={i} className="text-sm text-penta-black flex items-start gap-2">
                  <span className="text-penta-mint">•</span>
                  {feature}
                </li>
              ))}
            </ul>
          )}

          {props.idealFor?.length > 0 && (
            <div className="mt-4">
              <span className="text-xs text-penta-grey-medium">Ideal for: </span>
              <span className="text-xs text-penta-black">{props.idealFor.join(', ')}</span>
            </div>
          )}

          <div className="flex gap-3 mt-6">
            <button
              onClick={handleAddToCart}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-penta-mint text-penta-on-accent rounded hover:bg-penta-mint/90 transition-colors"
            >
              <ShoppingCart size={16} />
              Add to Cart
            </button>
            <button
              onClick={handleViewDetails}
              className="flex items-center justify-center gap-2 px-4 py-2 border border-penta-mint text-penta-mint rounded hover:bg-penta-mint/10 transition-colors"
            >
              <Eye size={16} />
              View Details
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

/**
 * PriceAlert - Price drop alert suggestion
 */
const PriceAlert = memo(function PriceAlert({ element, onAction }) {
  const { props } = element;

  const handleSetAlert = () => {
    onAction?.({ name: 'set_price_alert', params: { productId: props.productId, targetPrice: props.targetPrice } });
  };

  return (
    <div className="my-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
      <div className="flex items-center gap-3">
        <Bell className="text-amber-600" size={20} />
        <div>
          <div className="text-sm font-medium text-penta-black">{props.title}</div>
          <div className="text-xs text-penta-grey-medium">
            Current: ${props.currentPrice?.toFixed(2)} → Target: ${props.targetPrice?.toFixed(2)}
          </div>
        </div>
      </div>
      <button
        onClick={handleSetAlert}
        className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded hover:bg-amber-700 transition-colors"
      >
        Set Alert
      </button>
    </div>
  );
});

/**
 * TextMessage - AI text response (can contain children like grids)
 */
const TextMessage = memo(function TextMessage({ element, children }) {
  const { props } = element;

  const variantClasses = {
    default: 'text-penta-black',
    info: 'text-blue-700 bg-blue-50 border-blue-200',
    warning: 'text-amber-700 bg-amber-50 border-amber-200',
    success: 'text-green-700 bg-green-50 border-green-200',
  };

  const variant = props.variant || 'default';
  const isStyled = variant !== 'default';

  return (
    <div className="my-2">
      <div className={isStyled ? `p-3 rounded border ${variantClasses[variant]}` : variantClasses.default}>
        <ReactMarkdown className="prose prose-sm max-w-none">
          {props.content}
        </ReactMarkdown>
      </div>
      {children}
    </div>
  );
});

/**
 * ConversationalMessage - AI conversational response (non-product queries)
 * Used for greetings, preference questions, and general conversation.
 */
const ConversationalMessage = memo(function ConversationalMessage({ element, children }) {
  const { props } = element;

  return (
    <div className="my-3">
      <div className="flex items-start gap-3">
        {/* AI avatar indicator */}
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-penta-mint to-penta-mint/70 flex items-center justify-center">
          <Sparkles size={16} className="text-penta-on-accent" />
        </div>
        {/* Message content */}
        <div className="flex-1 min-w-0">
          <div className="bg-white border border-neutral-200 rounded-lg p-4 shadow-sm">
            <ReactMarkdown className="prose prose-sm max-w-none text-penta-black">
              {props.content}
            </ReactMarkdown>
          </div>
          {/* Render children (like SuggestionChips) below the message */}
          {children}
        </div>
      </div>
    </div>
  );
});

/**
 * SuggestionChips - Follow-up action suggestions
 * Clicking a chip either:
 * - Triggers an app action (connect_wallet, etc.) if action field is set
 * - Sends a new message to continue the conversation if query field is set
 */
const SuggestionChips = memo(function SuggestionChips({ element }) {
  const { props } = element;
  const { sendMessage } = useChatCallbacks();
  const { connectWallet } = useWallet();
  const [dismissed, setDismissed] = useState(false);
  const hasTESFired = useRef(false);

  // T026: Emit nudge.displayed TES event when nudge chips render
  useEffect(() => {
    if (!hasTESFired.current && props.suggestions?.length > 0) {
      const nudgePattern = props.suggestions[0]?.nudgePattern;
      if (nudgePattern) {
        hasTESFired.current = true;
        console.log('[TES Event] nudge.displayed', {
          pattern_type: nudgePattern,
          options: props.suggestions.map(s => s.label),
        });
      }
    }
  }, [props.suggestions]);

  const handleChipClick = async (suggestion) => {
    console.log('[SuggestionChips] Clicked:', suggestion);

    // T025: Fade out all chips after one is tapped
    setDismissed(true);

    // T027: Emit nudge.selected TES event if this is a nudge chip
    if (suggestion.nudgePattern) {
      console.log('[TES Event] nudge.selected', {
        option: suggestion.label,
        pattern_type: suggestion.nudgePattern,
      });
    }

    // Handle special actions - actions and queries are mutually exclusive
    if (suggestion.action) {
      console.log('[SuggestionChips] Executing action:', suggestion.action);
      switch (suggestion.action) {
        case 'connect_wallet':
          if (connectWallet) {
            await connectWallet();
          }
          return;
        default:
          console.warn('[SuggestionChips] Unknown action:', suggestion.action);
          return; // Don't fall through to query handling for unknown actions
      }
    }

    // Handle search queries (only if no action was set)
    if (suggestion.query && sendMessage) {
      console.log('[SuggestionChips] Calling sendMessage with:', suggestion.query);
      sendMessage(suggestion.query);
    } else {
      console.warn('[SuggestionChips] No sendMessage available or no query');
    }
  };

  if (!props.suggestions?.length) return null;

  return (
    <div className={`my-3 flex flex-wrap gap-2 transition-opacity duration-200 ${dismissed ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
      {props.suggestions.map((suggestion, i) => (
        <button
          key={i}
          onClick={() => handleChipClick(suggestion)}
          className="px-3 py-1.5 text-sm bg-penta-grey-light text-penta-black rounded-full hover:bg-penta-mint hover:text-penta-on-accent transition-colors cursor-pointer"
        >
          {suggestion.label}
        </button>
      ))}
    </div>
  );
});

/**
 * ClarificationPrompt - CLM clarifying question with quick options
 * Displays when CLM needs more information to better understand the query
 */
const ClarificationPrompt = memo(function ClarificationPrompt({ element }) {
  const { props } = element;
  const { sendMessage } = useChatCallbacks();

  const handleOptionClick = (option) => {
    console.log('[ClarificationPrompt] Option clicked:', option);
    if (sendMessage) {
      sendMessage(option);
    }
  };

  if (!props.question) return null;

  return (
    <div className="my-4 p-4 bg-gradient-to-r from-penta-mint/10 to-transparent border border-penta-mint/30 rounded-lg">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-penta-mint/20 rounded-full">
          <Sparkles size={16} className="text-penta-mint" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-penta-black mb-3">
            {props.question}
          </p>
          {props.options?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {props.options.map((option, i) => (
                <button
                  key={i}
                  onClick={() => handleOptionClick(option)}
                  className="px-3 py-1.5 text-sm bg-white border border-penta-mint/50 text-penta-black rounded-full hover:bg-penta-mint hover:text-penta-on-accent hover:border-penta-mint transition-colors cursor-pointer"
                >
                  {option}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

/**
 * ProductSkeleton - Loading skeleton
 * Supports both grid and inline layouts
 */
const ProductSkeleton = memo(function ProductSkeleton({ element }) {
  const count = element.props?.count || 3;
  const layout = element.props?.layout || 'grid';

  // Inline skeleton for conversational UI
  if (layout === 'inline') {
    return (
      <div className="space-y-2">
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className="flex gap-3 p-3 bg-white border border-neutral-200 rounded-lg animate-pulse">
            {/* Image placeholder */}
            <div className="w-24 h-24 bg-penta-grey-light rounded flex-shrink-0" />
            {/* Content placeholder */}
            <div className="flex-1 min-w-0 flex flex-col justify-between py-1">
              <div>
                <div className="h-2 bg-penta-grey-light rounded w-16 mb-2" />
                <div className="h-4 bg-penta-grey-light rounded w-3/4 mb-2" />
                <div className="h-3 bg-penta-grey-light rounded w-20" />
              </div>
              <div className="h-3 bg-penta-grey-light rounded w-full mt-2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Default grid skeleton
  return (
    <div className="my-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="p-3 bg-white border border-neutral-200 animate-pulse">
          <div className="aspect-square bg-penta-grey-light rounded mb-2" />
          <div className="h-3 bg-penta-grey-light rounded w-1/3 mb-1" />
          <div className="h-4 bg-penta-grey-light rounded w-2/3 mb-1" />
          <div className="h-3 bg-penta-grey-light rounded w-1/2" />
        </div>
      ))}
    </div>
  );
});

/**
 * EmptyState - No results found
 */
const EmptyState = memo(function EmptyState({ element, onAction }) {
  const { props } = element;

  const handleSuggestion = () => {
    if (props.suggestion) {
      onAction?.({ name: 'refine_search', params: { query: props.suggestion } });
    }
  };

  return (
    <div className="my-4 p-8 text-center bg-penta-grey-light/50 rounded-lg">
      <div className="text-penta-grey-medium text-lg">{props.message}</div>
      {props.suggestion && (
        <button
          onClick={handleSuggestion}
          className="mt-4 text-penta-mint hover:underline"
        >
          Try: {props.suggestion}
        </button>
      )}
    </div>
  );
});

/**
 * BrandSpotlight - Brand feature section
 */
const BrandSpotlight = memo(function BrandSpotlight({ element, children }) {
  const { props } = element;

  return (
    <div className="my-4 p-4 bg-gradient-to-r from-penta-grey-light/50 to-white border border-neutral-200 rounded-lg">
      <div className="flex items-center gap-3 mb-4">
        {props.logo && (
          <img
            src={resolveImageUrl(props.logo)}
            alt={props.brand}
            className="h-8 w-auto object-contain"
          />
        )}
        <div>
          <h3 className="text-lg font-semibold text-penta-black">{props.brand}</h3>
          {props.description && (
            <p className="text-sm text-penta-grey-medium">{props.description}</p>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {children}
      </div>
    </div>
  );
});

/**
 * Reaction button definitions for QuickReactions overlay
 */
const REACTION_BUTTONS = [
  { type: 'more_like_this', icon: Sparkles, label: 'More like this' },
  { type: 'different_colour', icon: Palette, label: 'Different colour' },
  { type: 'too_expensive', icon: TrendingDown, label: 'Too pricey' },
  { type: 'save', icon: Heart, label: 'Save' },
];

/**
 * QuickReactions — overlay of reaction buttons on a product card
 * Shows on tap (mobile) / hover (desktop).
 */
const QuickReactions = memo(function QuickReactions({ product, onReaction, hasReacted }) {
  const [visible, setVisible] = useState(false);
  const [animatingType, setAnimatingType] = useState(null);
  const dismissTimer = useRef(null);

  const handleToggle = (e) => {
    e.stopPropagation();
    setVisible(v => !v);
  };

  const handleReaction = (e, reactionType) => {
    e.stopPropagation();
    if (hasReacted(product.productId, reactionType)) return;

    setAnimatingType(reactionType);
    onReaction(product.productId, reactionType, {
      name: product.title,
      brand: product.brand,
      category: product.category || '',
      price: product.price,
      style_vibe: product.style_vibe,
      aesthetic: product.aesthetic,
      primary_colors: product.primary_colors,
    });

    // Auto-dismiss after 300ms
    if (dismissTimer.current) clearTimeout(dismissTimer.current);
    dismissTimer.current = setTimeout(() => {
      setVisible(false);
      setAnimatingType(null);
    }, 300);
  };

  return (
    <>
      {/* Trigger button — bottom-right of image */}
      <button
        onClick={handleToggle}
        className="absolute bottom-1.5 right-1.5 p-1.5 bg-white/90 rounded-full shadow opacity-0 group-hover:opacity-100 transition-opacity z-10"
        title="React to this product"
      >
        <Sparkles size={14} className="text-penta-mint" />
      </button>

      {/* Overlay with 4 reaction buttons */}
      {visible && (
        <div
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleToggle(); }}
          className="absolute inset-0 bg-black/40 flex items-center justify-center gap-2 z-20 animate-in fade-in duration-150"
          onClick={handleToggle}
        >
          {REACTION_BUTTONS.map(({ type, icon: Icon, label }) => {
            const reacted = hasReacted(product.productId, type);
            const isAnimating = animatingType === type;
            return (
              <button
                key={type}
                onClick={(e) => handleReaction(e, type)}
                disabled={reacted}
                className={`p-2 sm:p-2.5 rounded-full transition-all duration-200 min-w-[44px] min-h-[44px] flex flex-col items-center justify-center gap-0.5 ${
                  reacted
                    ? 'bg-penta-mint text-penta-on-accent'
                    : isAnimating
                    ? 'bg-penta-mint text-penta-on-accent scale-110'
                    : 'bg-white/90 text-penta-black hover:bg-penta-mint hover:text-penta-on-accent'
                }`}
                title={label}
              >
                <Icon size={18} className={reacted || isAnimating ? 'fill-current' : ''} />
                <span className="hidden sm:block text-[9px] leading-none">{label}</span>
              </button>
            );
          })}
        </div>
      )}
    </>
  );
});

/**
 * VisualPulse - Compact horizontal strip of product image cards
 * with progressive reveal (Show more) and sale price strikethrough.
 * Preferred over ProductGrid for product search results.
 */
const VisualPulse = memo(function VisualPulse({ element, onAction }) {
  const { props } = element;
  const { openProduct } = usePreviewPanel();
  const { sendMessage } = useChatCallbacks();
  const products = props.products || [];
  const initialCount = props.initialCount || 4;
  const batchSize = props.batchSize || 4;
  const _totalAvailable = props.totalAvailable;

  const [visibleCount, setVisibleCount] = useState(initialCount);
  const [toastMessage, setToastMessage] = useState(null);
  const hasTESFired = useRef(false);
  const scrollContainerRef = useRef(null);
  const scrollLeftBeforeExpand = useRef(0);

  // T021: Integrate useProductReactions hook
  const { hasReacted, addReaction, debouncedReaction } = useProductReactions();

  // T010: Emit products.displayed TES event on mount
  useEffect(() => {
    if (products.length > 0 && !hasTESFired.current) {
      hasTESFired.current = true;
      console.log('[TES Event] products.displayed', {
        count: products.length,
        layout: 'visual_pulse',
      });
    }
  }, [products.length]);

  // T016: Restore scroll position after expand
  useLayoutEffect(() => {
    if (scrollContainerRef.current && scrollLeftBeforeExpand.current > 0) {
      scrollContainerRef.current.scrollLeft = scrollLeftBeforeExpand.current;
    }
  }, [visibleCount]);

  const visibleProducts = products.slice(0, visibleCount);
  const hasMore = visibleCount < products.length;
  const remainingCount = products.length - visibleCount;
  const fullyRevealed = !hasMore;

  const handleShowMore = () => {
    // T016: Capture scroll position before state update
    if (scrollContainerRef.current) {
      scrollLeftBeforeExpand.current = scrollContainerRef.current.scrollLeft;
    }
    const newCount = Math.min(visibleCount + batchSize, products.length);
    setVisibleCount(newCount);

    // T017: Emit products.expanded TES event
    console.log('[TES Event] products.expanded', {
      count_after: newCount,
      total_available: products.length,
    });
  };

  const handleProductTap = (product) => {
    openProduct(product.productId, {
      id: product.productId,
      productId: product.productId,
      title: product.title,
      brand: product.brand,
      price: product.price,
      image: product.image,
    });
    onAction?.({ name: 'view_details', params: { productId: product.productId } });
  };

  const handleRefineSearch = () => {
    if (sendMessage) {
      sendMessage('Show me different options');
    }
  };

  // T021 + T022: Handle reaction with debouncing for rapid taps
  const handleReaction = useCallback((productId, reactionType, productAttributes) => {
    // Record reaction immediately (for UI state)
    addReaction(productId, reactionType, productAttributes);

    // T022: Debounce the actual action dispatch
    debouncedReaction(productId, reactionType, productAttributes, (pending) => {
      // Show toast for search-triggering reactions
      if (pending.reactionType !== 'save') {
        setToastMessage('Got it — searching based on your feedback');
        setTimeout(() => setToastMessage(null), 2000);
      }

      onAction?.({
        name: 'quick_reaction',
        params: {
          productId: pending.productId,
          reactionType: pending.reactionType,
          productAttributes: pending.productAttributes,
        },
      });
    });
  }, [addReaction, debouncedReaction, onAction]);

  // T040: Handle 0-result state in VisualPulse
  if (products.length === 0) {
    return (
      <div className="my-3 p-6 text-center bg-penta-grey-light/50 rounded-lg">
        <div className="text-penta-grey-medium">No matching products found</div>
        <button
          onClick={handleRefineSearch}
          className="mt-2 text-sm text-penta-mint hover:underline"
        >
          Try a different search
        </button>
      </div>
    );
  }

  // T011: Single-result edge case
  if (products.length === 1) {
    const product = products[0];
    return (
      <div className="my-3">
        <div
          onClick={() => handleProductTap(product)}
          className="cursor-pointer bg-white border border-neutral-200 hover:border-penta-mint transition-colors overflow-hidden rounded-lg"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleProductTap(product)}
        >
          <div className="relative aspect-[4/3] overflow-hidden bg-neutral-50">
            <img
              src={resolveImageUrl(product.image)}
              alt={product.title || 'Product image'}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={(e) => { e.target.src = '/images/placeholder.svg'; }}
            />
          </div>
          <div className="p-3">
            {product.brand && (
              <div className="text-[10px] text-penta-grey-medium uppercase tracking-wide truncate">
                {product.brand}
              </div>
            )}
            <div className="text-sm font-medium text-penta-black line-clamp-2">{product.title}</div>
            <div className="text-sm font-semibold text-penta-black mt-1 flex items-center gap-2">
              <span>£{product.price?.toFixed(2)}</span>
              {product.originalPrice && product.originalPrice > product.price && (
                <span className="line-through text-xs text-penta-grey-medium font-normal">£{product.originalPrice?.toFixed(2)}</span>
              )}
            </div>
          </div>
        </div>
        <p className="text-xs text-penta-grey-medium mt-2">
          This is the closest match. Want me to broaden the search?
        </p>
      </div>
    );
  }

  // Multi-product horizontal strip
  return (
    <div className="my-3 space-y-2">
      <div
        ref={scrollContainerRef}
        className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-neutral-300 scrollbar-track-transparent -mx-2 px-2"
      >
        {visibleProducts.map((product) => (
          <div
            key={product.productId}
            onClick={() => handleProductTap(product)}
            className="flex-shrink-0 w-[44vw] sm:w-40 cursor-pointer bg-white border border-neutral-200 hover:border-penta-mint transition-all duration-200 rounded-lg overflow-hidden hover:shadow-md group"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleProductTap(product)}
          >
            {/* Square image with reaction overlay */}
            <div className="relative w-full aspect-square overflow-hidden bg-neutral-50">
              <img
                src={resolveImageUrl(product.image)}
                alt={product.title || 'Product image'}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => { e.target.src = '/images/placeholder.svg'; }}
              />
              {/* Quality tier badge */}
              {product.qualityTier && QUALITY_BADGES[product.qualityTier.toLowerCase()] && (() => {
                const badge = QUALITY_BADGES[product.qualityTier.toLowerCase()];
                const BadgeIcon = badge.icon;
                return (
                  <div className={`absolute top-1 left-1 px-1.5 py-0.5 text-[10px] font-medium rounded flex items-center gap-0.5 ${badge.className}`}>
                    {BadgeIcon && <BadgeIcon size={10} />}
                    {badge.label}
                  </div>
                );
              })()}
              {/* T020: Quick reactions overlay */}
              <QuickReactions
                product={product}
                onReaction={handleReaction}
                hasReacted={hasReacted}
              />
            </div>

            {/* Card details */}
            <div className="p-2.5">
              {product.brand && (
                <div className="text-[10px] text-penta-grey-medium uppercase tracking-wide truncate">
                  {product.brand}
                </div>
              )}
              <div className="text-xs font-medium text-penta-black line-clamp-2 leading-snug h-8">
                {product.title}
              </div>
              <div className="text-sm font-semibold text-penta-black mt-1 flex items-center gap-2">
                <span>£{product.price?.toFixed(2)}</span>
                {product.originalPrice && product.originalPrice > product.price && (
                  <span className="line-through text-xs text-penta-grey-medium font-normal">£{product.originalPrice?.toFixed(2)}</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Show more / Refine CTA */}
      <div className="flex items-center gap-3">
        {hasMore && (
          <button
            onClick={handleShowMore}
            className="bg-penta-grey-light text-penta-black rounded-full px-4 py-2 text-sm hover:bg-neutral-200 transition-colors"
          >
            Show {Math.min(remainingCount, batchSize)} more
          </button>
        )}
        {fullyRevealed && products.length >= 24 && (
          <button
            onClick={handleRefineSearch}
            className="text-sm bg-penta-mint text-penta-on-accent px-4 py-2 rounded-full hover:bg-penta-mint/90 transition-colors"
          >
            Refine your search to see more
          </button>
        )}
      </div>

      {/* T022: Toast for debounced reaction feedback */}
      {toastMessage && (
        <div className="text-xs text-penta-grey-medium bg-penta-grey-light/50 rounded px-3 py-1.5 animate-in fade-in duration-200">
          {toastMessage}
        </div>
      )}
    </div>
  );
});

/**
 * ExplorationGrid - Full-width visual browsing grid for "just looking" intent.
 * Large image tiles with gradient overlay for name/price.
 * Scroll-depth tracking via scroll listener. "Back to chat" floating button.
 */
const ExplorationGrid = memo(function ExplorationGrid({ element, onAction }) {
  const { props } = element;
  const { openProduct } = usePreviewPanel();
  const products = props.products || [];
  const title = props.title || 'Explore';

  const hasTESFired = useRef(false);
  const gridContainerRef = useRef(null);
  const milestonesFired = useRef(new Set());
  const enterTimeRef = useRef(Date.now());
  const viewedCountRef = useRef(0);

  // T033: Emit exploration.entered TES event on mount
  useEffect(() => {
    if (!hasTESFired.current && products.length > 0) {
      hasTESFired.current = true;
      enterTimeRef.current = Date.now();
      console.log('[TES Event] exploration.entered', {
        product_count: products.length,
        trigger_query: title,
      });
    }
  }, [products.length, title]);

  // T033: Track scroll depth milestones via nearest scrollable ancestor
  useEffect(() => {
    const gridEl = gridContainerRef.current;
    if (!gridEl) return;

    // Find the scrollable ancestor (the page scroll container)
    const scrollParent = gridEl.closest('[class*="overflow-y-auto"]') || window;
    const isWindow = scrollParent === window;

    const handleScroll = () => {
      const scrollTop = isWindow ? window.scrollY : scrollParent.scrollTop;
      const scrollHeight = isWindow ? document.documentElement.scrollHeight : scrollParent.scrollHeight;
      const clientHeight = isWindow ? window.innerHeight : scrollParent.clientHeight;
      if (scrollHeight <= clientHeight) return;

      const percentage = Math.round((scrollTop / (scrollHeight - clientHeight)) * 100);

      [25, 50, 75, 100].forEach(milestone => {
        if (percentage >= milestone && !milestonesFired.current.has(milestone)) {
          milestonesFired.current.add(milestone);
          console.log('[TES Event] exploration.scroll_depth', { percentage: milestone });
        }
      });
    };

    const target = isWindow ? window : scrollParent;
    target.addEventListener('scroll', handleScroll, { passive: true });
    return () => target.removeEventListener('scroll', handleScroll);
  }, []);

  const handleProductTap = (product) => {
    viewedCountRef.current += 1;
    openProduct(product.productId, {
      id: product.productId,
      productId: product.productId,
      title: product.title,
      brand: product.brand,
      price: product.price,
      image: product.image,
    });
    onAction?.({ name: 'view_details', params: { productId: product.productId } });
  };

  const handleBackToChat = () => {
    // T033: Emit exploration.exited TES event
    console.log('[TES Event] exploration.exited', {
      exit_method: 'back_button',
      products_viewed: viewedCountRef.current,
      duration_ms: Date.now() - enterTimeRef.current,
    });
    onAction?.({ name: 'exit_exploration', params: {} });
  };

  // T040: Handle 0-result state in ExplorationGrid
  if (products.length === 0) {
    return (
      <div ref={gridContainerRef} className="my-3 p-6 text-center bg-penta-grey-light/50 rounded-lg">
        <div className="text-penta-grey-medium">Nothing to explore right now</div>
        <button
          onClick={handleBackToChat}
          className="mt-2 text-sm text-penta-mint hover:underline"
        >
          ← Back to chat
        </button>
      </div>
    );
  }

  return (
    <div ref={gridContainerRef} className="my-3 relative">
      {title && (
        <h3 className="text-lg font-semibold text-penta-black mb-3">{title}</h3>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {products.map((product) => (
          <div
            key={product.productId}
            onClick={() => handleProductTap(product)}
            className="relative cursor-pointer overflow-hidden rounded-lg group aspect-[3/4]"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleProductTap(product)}
          >
            <img
              src={resolveImageUrl(product.image)}
              alt={product.title || 'Product image'}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              loading="lazy"
              onError={(e) => { e.target.src = '/images/placeholder.svg'; }}
            />
            {/* Gradient overlay with name/price */}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-3 pt-8">
              {product.brand && (
                <div className="text-[10px] text-white/80 uppercase tracking-wide truncate">
                  {product.brand}
                </div>
              )}
              <div className="text-sm font-medium text-white line-clamp-1">{product.title}</div>
              <div className="text-sm font-semibold text-white mt-0.5">
                £{product.price?.toFixed(2)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Back to chat floating button */}
      <button
        onClick={handleBackToChat}
        className="sticky bottom-4 float-right mr-2 mt-4 px-4 py-2 bg-white/90 backdrop-blur text-penta-black text-sm rounded-full shadow-lg hover:bg-white transition-colors z-30"
      >
        ← Back to chat
      </button>
    </div>
  );
});

/**
 * Component registry for json-render
 */
export const componentRegistry = {
  ProductCard,
  InlineProductCard,
  ProductGrid,
  CompareTable,
  FeaturedProduct,
  PriceAlert,
  TextMessage,
  ConversationalMessage,
  SuggestionChips,
  ClarificationPrompt,
  ProductSkeleton,
  EmptyState,
  BrandSpotlight,
  VisualPulse,
  ExplorationGrid,
};

export default componentRegistry;
